import clsx from "clsx";
import merge from "lodash/merge";
import { FunctionComponent, JSX } from "react";

import { type ContentCardArticle } from "@ui/components/content-cards/content-cards.types";
import ProminentContentCard from "@ui/components/content-cards/ProminentContentCard/ProminentContentCard";
import { type Pattern } from "@ui/components/patterns/patterns.types";
import { useComponentConfig } from "@ui/context/component-config-context";
import { getContexts } from "@ui/utils/patterns";

import * as styles from "./ThreeUpPattern.css";

// Each card might have a different component context; set some defaults
// but lets also allow passing them in (e.g. from the CategoryPillar)
const DEFAULT_DESKTOP_CONTEXTS = [
  "threeUpPatternLargeFirst",
  "threeUpPatternLargeSecond",
  "threeUpPatternLargeThird",
];

const DEFAULT_MOBILE_CONTEXTS = [
  "threeUpPatternFirst",
  "threeUpPatternSecond",
  "threeUpPatternThird",
];

export interface Props extends Pattern {
  componentContext?: string;
}

const ThreeUpPattern: FunctionComponent<Props> = (props) => {
  const threeUpPatternConfig = useComponentConfig(
    "threeUpPattern",
    props.componentContext || "",
  );

  const {
    cardsData,
    mobileCardContext = DEFAULT_MOBILE_CONTEXTS,
    desktopCardContext = DEFAULT_DESKTOP_CONTEXTS,
    gaComponentName = "three_up_pattern",
    desktopContainerClassName,
    className,
  } = merge({}, threeUpPatternConfig, props);

  const { mobileContext, desktopContext } = getContexts(
    cardsData.length,
    mobileCardContext,
    desktopCardContext,
  );

  const getContainerClass = (position: number): string => {
    return (
      styles.contentContainer[
        (position + 1).toString() as keyof typeof styles.contentContainer
      ] || styles.contentContainer["1"]
    );
  };

  const getDesktopCard = (entry: ContentCardArticle, position: number) => {
    return (
      <div className={getContainerClass(position)} key={entry.id}>
        <ProminentContentCard
          {...entry}
          componentContext={desktopContext[position]}
          gaComponentName={gaComponentName}
        />
      </div>
    );
  };

  const getMobileCard = (entry: ContentCardArticle, position: number) => {
    return (
      <div className={getContainerClass(position)} key={entry.id}>
        <ProminentContentCard
          {...entry}
          componentContext={mobileContext[position]}
          gaComponentName={gaComponentName}
        />
      </div>
    );
  };

  const desktopCards: Array<JSX.Element> = [];
  const mobileCards: Array<JSX.Element> = [];

  cardsData.forEach((entry, index) => {
    desktopCards.push(getDesktopCard(entry, index));
    mobileCards.push(getMobileCard(entry, index));
  });

  return (
    <div className={className}>
      <div className={clsx(styles.desktopContainer, desktopContainerClassName)}>
        {desktopCards}
      </div>
      <div className={styles.mobileContainer}>{mobileCards}</div>
    </div>
  );
};

export default ThreeUpPattern;
